import { ArrowDownOutlined, ArrowUpOutlined, DownOutlined, ExclamationCircleOutlined, FileAddOutlined, OrderedListOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Input, Menu, Row, Select, Space } from "antd";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import { BatchRangeConfirmation, CardTitleCsvImport } from "models/cardIngestion";
import { PackageTypeWeight, PackageTypeWeightNames } from "models/packages";
import { FC, KeyboardEvent, useState } from "react";
import CardsReadyForUploadModal from "./CardsReadyForUploadModal";
import CardTitleCsvImportModal from "./CardTitleCsvImportModal";
import CardTitleRulesModal from "./CardTitleRulesModal";
import ChangePackageIdModal from "./ChangePackageIdModal";
import ConfirmBatchesReviewedModal from "./ConfirmBatchesReviewedModal";
import CurrentCardTitleCsvImportStatus from "./CurrentCardTitleCsvImportStatus";
import DoNotUploadModal from "./DoNotUploadModal";

const ChangePackageIdAction = 0;
const DoNotUploadAction = 1;
const CardsReadyToUploadAction = 2;

const { Option } = Select;

interface CardQAToolbarProps {
    isAdmin: boolean;
    onSearch: (term: string) => void;
    onSearchClear?: () => void;
    selectedRowCount: number;
    searchFailed: boolean;
    onBulkDoNotUpload?: () => Promise<void>;
    onBulkPackageIdUpdate?: (newPackageId: string) => Promise<void>;
    onConfirmBatches: (startBatch: string, endBatch: string) => Promise<BatchRangeConfirmation>;
    onBatchesPassedQA?: (startBatch: string, endBatch: string) => Promise<void>;
    onCardsPassedQA?: () => Promise<void>;
    onCsvImportFinished?: (rec: CardTitleCsvImport) => Promise<void>;
    onPackageTypeChanged?: (packageTypeId: string | null) => void;
    onPackageWeightChanged?: (weight: PackageTypeWeight | null) => void;
    onUpdatedRules?: () => Promise<void>;
    onViewTypeChanged?: (doNotUpload: boolean, fixesNeeded: boolean) => void;
    showJumpers?: boolean;
}

const CardQAToolbar: FC<CardQAToolbarProps> = ({
    isAdmin,
    onSearch,
    onSearchClear,
    selectedRowCount,
    searchFailed,
    onBulkDoNotUpload,
    onBulkPackageIdUpdate,
    onConfirmBatches,
    onBatchesPassedQA,
    onCardsPassedQA,
    onCsvImportFinished,
    onPackageTypeChanged,
    onPackageWeightChanged,
    onUpdatedRules,
    onViewTypeChanged,
    showJumpers = false
}) => {
    const { data: packageTypes = [] } = useAvailablePackageTypes();
    const [packageIdModalOpen, setPackageIdModalOpen] = useState(false);
    const [doNotUploadModalOpen, setDoNotUploadModalOpen] = useState(false);
    const [batchesReadyForUploadModalOpen, setBatchesReadyForUploadModalOpen] = useState(false);
    const [cardsReadyForUploadModalOpen, setCardsReadyForUploadModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [filter, setFilter] = useState("In QA");
    const noRowsSelected = selectedRowCount === 0;

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && e.currentTarget.value.trim() !== '') {
            onSearch(e.currentTarget.value);
        }
    }

    return <Row align="middle">
        <Col span={24}>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Dropdown.Button
                    type="primary"
                    icon={<DownOutlined />}
                    overlay={<Menu items={[
                        {
                            label: 'In QA',
                            key: 'inQA',
                            onClick: () => {
                                setFilter("In QA");
                                onViewTypeChanged && onViewTypeChanged(false, false);
                            }
                        },
                        {
                            label: 'Fixes Needed',
                            key: 'fixesNeeded',
                            onClick: () => {
                                setFilter("Fixes Needed");
                                onViewTypeChanged && onViewTypeChanged(false, true);
                            }
                        },
                        {
                            label: 'Do Not Upload',
                            key: 'DoNotUpload',
                            onClick: () => {
                                setFilter("Do Not Upload");
                                onViewTypeChanged && onViewTypeChanged(true, false);
                            }
                        }]}
                    />}
                >
                    {filter}
                </Dropdown.Button>

                <Select
                    style={{ minWidth: '150px' }}
                    defaultValue=""
                    onChange={(value: string) => {
                        if (!onPackageWeightChanged) {
                            return;
                        }

                        if (value === "") {
                            onPackageWeightChanged(null);
                        } else {
                            const weight = value as unknown as PackageTypeWeight;
                            onPackageWeightChanged(weight);
                        }
                    }}
                    options={[
                        { label: '- All Pkg. Weights -', value: '' },
                        { label: PackageTypeWeightNames[PackageTypeWeight.Premium], value: PackageTypeWeight.Premium },
                        { label: PackageTypeWeightNames[PackageTypeWeight.Priority], value: PackageTypeWeight.Priority },
                        { label: PackageTypeWeightNames[PackageTypeWeight.Standard], value: PackageTypeWeight.Standard }
                    ]}
                >

                </Select>

                <Select // Currently hidden, might make a comeback
                    style={{ minWidth: '180px', display: 'none' }}
                    defaultValue=""
                    onChange={(id: string) => {
                        if (!onPackageTypeChanged) {
                            return;
                        }

                        const packageTypeId = id.trim();
                        if (packageTypeId === "") {
                            onPackageTypeChanged(null);
                        } else {
                            onPackageTypeChanged(packageTypeId);
                        }
                    }}
                >
                    <Option value="">- All Package Types -</Option>
                    {packageTypes.map(pt => (
                        <Option key={pt.id} value={pt.id}>{pt.internalName}</Option>
                    ))}
                </Select>

                <Dropdown.Button
                    icon={<DownOutlined />}
                    type={noRowsSelected ? 'default' : 'primary'}
                    overlay={<Menu items={[
                        {
                            label: 'Change Package ID',
                            key: ChangePackageIdAction,
                            disabled: noRowsSelected,
                            onClick: () => setPackageIdModalOpen(true)
                        },
                        {
                            label: 'Do Not Upload',
                            key: DoNotUploadAction,
                            disabled: noRowsSelected,
                            onClick: () => setDoNotUploadModalOpen(true)
                        },
                        {
                            label: 'Ready for Upload',
                            key: CardsReadyToUploadAction,
                            disabled: noRowsSelected,
                            onClick: () => setCardsReadyForUploadModalOpen(true)
                        }
                    ]} />}
                >
                    {noRowsSelected ? 'No' : selectedRowCount} Row{selectedRowCount !== 1 ? 's' : ''} Selected
                </Dropdown.Button>

                <Space>
                    <div>Jump To:</div>
                    <Input
                        onKeyDown={onKeyDown}
                        placeholder="Batch Num or Pkg ID"
                        prefix={searchFailed ? <ExclamationCircleOutlined title="No Results!" /> : <span />}
                        status={searchFailed ? 'error' : ''}
                        allowClear
                        onChange={e => {
                            if (e.currentTarget.value === '' && onSearchClear) {
                                onSearchClear();
                            }
                        }}
                    />
                </Space>

                <Button type="link" onClick={() => setBatchesReadyForUploadModalOpen(true)} icon={<UploadOutlined />}>
                    Ready for Upload ...
                </Button>

                {isAdmin && <Button type="link" onClick={() => setImportModalOpen(true)} icon={<FileAddOutlined />}>
                    Import Card Title CSV ...
                </Button>}

                {isAdmin && <Button type="link" onClick={() => setRulesModalOpen(true)} icon={<OrderedListOutlined />}>
                    Title Rules
                </Button>}

                {isAdmin && <CurrentCardTitleCsvImportStatus
                    onCsvImportFinished={async (rec: CardTitleCsvImport) => {
                        if (onCsvImportFinished) {
                            await onCsvImportFinished(rec);
                        }
                    }}
                />}

                {showJumpers && <Space direction="vertical" style={{ flex: 1 }}>
                    <Button size="small" icon={<ArrowUpOutlined />} type="primary" onClick={() =>
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                    />
                    <Button size="small" icon={<ArrowDownOutlined />} type="primary" onClick={() =>
                        window.scrollTo({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' })}
                    />
                </Space>}
            </Space>
        </Col>

        <Col span={24}>
            <ChangePackageIdModal
                open={packageIdModalOpen}
                affectedCount={selectedRowCount}
                onCancel={() => setPackageIdModalOpen(false)}
                onConfirm={async (newPackageId: string) => {
                    if (onBulkPackageIdUpdate) {
                        await onBulkPackageIdUpdate(newPackageId)
                    }
                    setPackageIdModalOpen(false)
                }}
            />

            <DoNotUploadModal
                open={doNotUploadModalOpen}
                affectedCount={selectedRowCount}
                onCancel={() => setDoNotUploadModalOpen(false)}
                onConfirm={async () => {
                    if (onBulkDoNotUpload) {
                        await onBulkDoNotUpload()
                    }
                    setDoNotUploadModalOpen(false)
                }}
            />

            <ConfirmBatchesReviewedModal
                open={batchesReadyForUploadModalOpen}
                onCancel={() => setBatchesReadyForUploadModalOpen(false)}
                onCheck={async (startBatch: string, endBatch: string) => {
                    return await onConfirmBatches(startBatch, endBatch);
                }}
                onConfirm={async (startBatch: string, endBatch: string) => {
                    if (onBatchesPassedQA) {
                        await onBatchesPassedQA(startBatch, endBatch)
                    }
                    setBatchesReadyForUploadModalOpen(false)
                }}
            />

            <CardsReadyForUploadModal
                open={cardsReadyForUploadModalOpen}
                affectedCount={selectedRowCount}
                onCancel={() => setCardsReadyForUploadModalOpen(false)}
                onConfirm={async () => {
                    if (onCardsPassedQA) {
                        await onCardsPassedQA();
                    }
                    setCardsReadyForUploadModalOpen(false)
                }}
            />

            <CardTitleCsvImportModal
                open={importModalOpen}
                onCancel={() => setImportModalOpen(false)}
            />

            <CardTitleRulesModal
                open={rulesModalOpen}
                onCancel={() => setRulesModalOpen(false)}
                onComplete={async () => {
                    if (onUpdatedRules) {
                        await onUpdatedRules();
                    }
                    setRulesModalOpen(false);
                }}
            />
        </Col>
    </Row>;
};

export default CardQAToolbar;